import { useQuery, useMutation, type UseQueryOptions } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type {
  PaymentPlan,
  Wallet,
  WalletTransaction,
  CommunityFund,
  HospitalPartner,
  HealthcareProvider,
  Facility,
  PreCareSubscription,
  SuccessStory,
  Notification,
  EmployerPartner,
  EmployerEmployee,
  EmployerContribution,
  FeatureFlag,
  User,
} from "@shared/schema";

type QueryOpts<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export function usePaymentPlans(opts?: QueryOpts<PaymentPlan[]>) {
  return useQuery<PaymentPlan[]>({ queryKey: ["/api/payment-plans"], ...opts });
}

export function useWallet(opts?: QueryOpts<Wallet>) {
  return useQuery<Wallet>({ queryKey: ["/api/wallet"], ...opts });
}

export function useWallets(opts?: QueryOpts<Wallet[]>) {
  return useQuery<Wallet[]>({ queryKey: ["/api/wallets"], ...opts });
}

export function useMyWallets(opts?: QueryOpts<Wallet[]>) {
  return useQuery<Wallet[]>({ queryKey: ["/api/my-wallets"], ...opts });
}

export function useWalletTransactions(opts?: QueryOpts<WalletTransaction[]>) {
  return useQuery<WalletTransaction[]>({ queryKey: ["/api/wallet/transactions"], ...opts });
}

export function useFullWalletAccess(opts?: QueryOpts<any[]>) {
  return useQuery<any[]>({ queryKey: ["/api/user/full-wallet-access"], ...opts });
}

export function useCommunityFunds(opts?: QueryOpts<CommunityFund[]>) {
  return useQuery<CommunityFund[]>({ queryKey: ["/api/community-funds"], ...opts });
}

export function useHospitalPartners(opts?: QueryOpts<HospitalPartner[]>) {
  return useQuery<HospitalPartner[]>({ queryKey: ["/api/hospital-partners"], ...opts });
}

export function useHealthcareProviders(opts?: QueryOpts<HealthcareProvider[]>) {
  return useQuery<HealthcareProvider[]>({ queryKey: ["/api/healthcare-providers"], ...opts });
}

export function useFacilities(opts?: QueryOpts<Facility[]>) {
  return useQuery<Facility[]>({ queryKey: ["/api/facilities"], ...opts });
}

export function useSubscriptions(opts?: QueryOpts<PreCareSubscription[]>) {
  return useQuery<PreCareSubscription[]>({ queryKey: ["/api/subscriptions"], ...opts });
}

export function useSuccessStories(opts?: QueryOpts<SuccessStory[]>) {
  return useQuery<SuccessStory[]>({ queryKey: ["/api/success-stories"], ...opts });
}

export function useAdminSuccessStories(opts?: QueryOpts<SuccessStory[]>) {
  return useQuery<SuccessStory[]>({ queryKey: ["/api/admin/success-stories"], ...opts });
}

export function useNotifications(opts?: QueryOpts<Notification[]>) {
  return useQuery<Notification[]>({ queryKey: ["/api/notifications"], ...opts });
}

export function useUnreadNotificationsCount(opts?: QueryOpts<{ count: number }>) {
  return useQuery<{ count: number }>({ queryKey: ["/api/notifications/unread-count"], ...opts });
}

export function useFeatureFlags(opts?: QueryOpts<FeatureFlag[]>) {
  return useQuery<FeatureFlag[]>({ queryKey: ["/api/feature-flags"], ...opts });
}

export function useAdminUsers(opts?: QueryOpts<User[]>) {
  return useQuery<User[]>({ queryKey: ["/api/admin/users"], ...opts });
}

export function useProfile(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/profile"], ...opts });
}

export function useSettings(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/settings"], ...opts });
}

export function useEmployerMyPartner(opts?: QueryOpts<EmployerPartner>) {
  return useQuery<EmployerPartner>({ queryKey: ["/api/employer/my-partner"], ...opts });
}

export function useEmployerEmployees(opts?: QueryOpts<EmployerEmployee[]>) {
  return useQuery<EmployerEmployee[]>({ queryKey: ["/api/employer/employees"], ...opts });
}

export function useEmployerContributions(opts?: QueryOpts<EmployerContribution[]>) {
  return useQuery<EmployerContribution[]>({ queryKey: ["/api/employer/contributions"], ...opts });
}

export function useEmployerPartners(opts?: QueryOpts<EmployerPartner[]>) {
  return useQuery<EmployerPartner[]>({ queryKey: ["/api/employer-partners"], ...opts });
}

export function useSmallBusinessDashboard(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/small-business/dashboard"], ...opts });
}

export function useConditionWalletsDashboard(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/admin/condition-wallets/dashboard"], ...opts });
}

export function usePlatformStats(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/admin/platform-stats"], ...opts });
}

export function useMedicalConditionsActive(opts?: QueryOpts<any[]>) {
  return useQuery<any[]>({ queryKey: ["/api/medical-conditions/active"], ...opts });
}

export function usePublicConditionWallets(opts?: QueryOpts<any[]>) {
  return useQuery<any[]>({ queryKey: ["/api/public/condition-wallets"], ...opts });
}

export function useCommunityImpact(opts?: QueryOpts<any>) {
  return useQuery<any>({ queryKey: ["/api/public/community-impact"], ...opts });
}

export function useWalletInvitationsPending(opts?: QueryOpts<any[]>) {
  return useQuery<any[]>({ queryKey: ["/api/wallet-invitations/pending"], ...opts });
}

export function useIncentiveLedger(opts?: QueryOpts<any[]>) {
  return useQuery<any[]>({ queryKey: ["/api/incentive-ledger"], ...opts });
}

export interface FinancialTransparencyStats {
  totalContributed: number;
  totalSpent: number;
  currentBalance: number;
  totalPayments: number;
  communityImpact: number;
  memberSince: string;
  activeSubscriptions: number;
  spendingCategories: { category: string; amount: number; percentage: number }[];
  recentTransactions: {
    id: string;
    type: string;
    amount: number;
    description: string | null;
    status: string;
    walletName: string;
    createdAt: string | null;
  }[];
}

export function useFinancialTransparencyStats(opts?: QueryOpts<FinancialTransparencyStats>) {
  return useQuery<FinancialTransparencyStats>({ queryKey: ["/api/financial-transparency/stats"], ...opts });
}

export function usePlatformFeeRates(opts?: QueryOpts<Record<string, number>>) {
  return useQuery<Record<string, number>>({ queryKey: ["/api/platform-fees/rates"], ...opts });
}

export const QUERY_KEYS = {
  paymentPlans: ["/api/payment-plans"] as const,
  wallet: ["/api/wallet"] as const,
  wallets: ["/api/wallets"] as const,
  myWallets: ["/api/my-wallets"] as const,
  walletTransactions: ["/api/wallet/transactions"] as const,
  fullWalletAccess: ["/api/user/full-wallet-access"] as const,
  communityFunds: ["/api/community-funds"] as const,
  hospitalPartners: ["/api/hospital-partners"] as const,
  healthcareProviders: ["/api/healthcare-providers"] as const,
  facilities: ["/api/facilities"] as const,
  subscriptions: ["/api/subscriptions"] as const,
  successStories: ["/api/success-stories"] as const,
  adminSuccessStories: ["/api/admin/success-stories"] as const,
  notifications: ["/api/notifications"] as const,
  unreadNotificationsCount: ["/api/notifications/unread-count"] as const,
  featureFlags: ["/api/feature-flags"] as const,
  adminUsers: ["/api/admin/users"] as const,
  profile: ["/api/profile"] as const,
  settings: ["/api/settings"] as const,
  employerMyPartner: ["/api/employer/my-partner"] as const,
  employerEmployees: ["/api/employer/employees"] as const,
  employerContributions: ["/api/employer/contributions"] as const,
  employerStats: ["/api/employer/stats"] as const,
  employerPartners: ["/api/employer-partners"] as const,
  smallBusinessDashboard: ["/api/small-business/dashboard"] as const,
  conditionWalletsDashboard: ["/api/admin/condition-wallets/dashboard"] as const,
  medicalConditionsActive: ["/api/medical-conditions/active"] as const,
  publicConditionWallets: ["/api/public/condition-wallets"] as const,
  communityImpact: ["/api/public/community-impact"] as const,
  walletInvitationsPending: ["/api/wallet-invitations/pending"] as const,
  incentiveLedger: ["/api/incentive-ledger"] as const,
  hardshipAssistanceRequests: ["/api/hardship-assistance"] as const,
  myHardshipAssistanceRequests: ["/api/hardship-assistance/my"] as const,
} as const;
